import { curveAria, CURVE_BOX } from './flowModel.js'
import { sourceAge } from './model.js'

// The design's "Flow": the win-probability curve and the list of swings. P6-330b (GN-3b).
//
// Hand-built SVG in a 300 by 100 box stretched over the plot (`preserveAspectRatio="none"`), strokes that do not
// scale, every label HTML beside it (text cannot live in a box that is stretched), the GN-2c chart style. The curve is
// `flowModel.js::curveModel`'s answer and the swings are `swingRows`'; this file draws what it is given. It is drawn
// at all only when there is a curve or a callout (`funModel.js::gameFun`), and each half only when it has something
// to say: a game with callouts and no curve (preseason) draws the list alone, with no heading over an empty plot.
//
// ═══════════════════════════════════════════════════════════════════════════
// TOP IS HOME, AND THE PAGE SAYS SO
// ═══════════════════════════════════════════════════════════════════════════
//
// The design puts the home team at the top of the plot and the away team at the bottom, with `50` between them on a
// dashed line, and the note says `up = HOU`. The two sides are filled in their own colours (the chart palette,
// `--hoops-away-line` and `--hoops-home-line`) from the 50 percent line out, so a curve above it is the home team
// ahead and the area under it is the home team's colour. The line itself is ink: a reference is never a team.
//
// ⚠️ THE NOTE CARRIES ESPN'S OWN AGE, not the box score's. The curve is ESPN's, `win_probability_as_of` is when ESPN
// last answered for the game, and the contract says a failing ESPN leaves the curve in place with its OLD age beside
// a current score. `NBA · 14s` in the hero and `ESPN · 7m` here are both true at once.

const GRADIENT = 'hoops-wp-gradient'

function Curve({ curve, away, home, note }) {
  return (
    <div className="d2d-hoops__wpcurve" data-testid="hoops-wp">
      <div className="d2d-hoops__panelhead">
        <h3 className="d2d-hoops__sectiontitle d2d-micro">Win probability</h3>
        <span className="d2d-hoops__panelnote d2d-micro" data-testid="hoops-wp-note">
          {note}
        </span>
      </div>
      <div className="d2d-hoops__plotgrid d2d-hoops__plotgrid--wp">
        <div className="d2d-hoops__axis" aria-hidden="true">
          <span className="d2d-hoops__axislabel d2d-hoops__axislabel--home" data-side="home">
            {home}
          </span>
          <span className="d2d-hoops__axislabel" style={{ top: '50%' }}>
            50
          </span>
          <span className="d2d-hoops__axislabel d2d-hoops__axislabel--away" data-side="away">
            {away}
          </span>
        </div>
        <div className="d2d-hoops__wpplot" role="img" aria-label={curveAria(curve, home)} data-testid="hoops-wp-plot">
          <svg
            viewBox={`0 0 ${CURVE_BOX.width} ${CURVE_BOX.height}`}
            preserveAspectRatio="none"
            focusable="false"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={GRADIENT} x1="0" y1="0" x2="0" y2={CURVE_BOX.height} gradientUnits="userSpaceOnUse">
                <stop offset="0" className="d2d-hoops__wpstop--home d2d-hoops__wpstop--far" />
                <stop offset="0.5" className="d2d-hoops__wpstop--home d2d-hoops__wpstop--near" />
                <stop offset="0.5" className="d2d-hoops__wpstop--away d2d-hoops__wpstop--near" />
                <stop offset="1" className="d2d-hoops__wpstop--away d2d-hoops__wpstop--far" />
              </linearGradient>
            </defs>
            {curve.starts.slice(1).map((start) => (
              <line
                key={start.period}
                className="d2d-hoops__grid"
                x1={start.x}
                x2={start.x}
                y1="0"
                y2={CURVE_BOX.height}
              />
            ))}
            <line className="d2d-hoops__wpmid" x1="0" x2={CURVE_BOX.width} y1="50" y2="50" />
            <path className="d2d-hoops__wparea" d={curve.area} fill={`url(#${GRADIENT})`} data-testid="hoops-wp-area" />
            <path className="d2d-hoops__wpline" d={curve.line} data-testid="hoops-wp-line" />
          </svg>
        </div>
        <span />
        <div className="d2d-hoops__months" aria-hidden="true">
          {curve.starts.map((start) => (
            <span key={start.period} className="d2d-hoops__month" style={{ left: `${Math.min(start.left, 90)}%` }}>
              {start.label}
            </span>
          ))}
        </div>
      </div>
    </div>
  )
}

function Swings({ swings }) {
  return (
    <div className="d2d-hoops__swings" data-testid="hoops-swings">
      <div className="d2d-hoops__panelhead">
        <h3 className="d2d-hoops__sectiontitle d2d-micro">Latest moments</h3>
        <span className="d2d-hoops__panelnote d2d-micro">newest first</span>
      </div>
      <ol className="d2d-hoops__swinglist">
        {swings.map((swing) => (
          <li className="d2d-hoops__swing" key={swing.key} data-kind={swing.kind} data-side={swing.side ?? 'none'} data-testid="hoops-swing">
            <span className="d2d-hoops__swingtick" aria-hidden="true" />
            <span className="d2d-hoops__swingline">{swing.line}</span>
            <span className="d2d-hoops__swingwhen d2d-micro">{swing.when}</span>
          </li>
        ))}
      </ol>
    </div>
  )
}

/**
 * The Flow section. `curve` and `swings` are the model's answers (either may be empty: `null` and `[]`), `away` and
 * `home` are the tricodes, `source` and `stampMs` are ESPN's own (the game's `win_probability_source`, and its
 * `win_probability_as_of` as an instant, or `null`) and `nowMs` is the page's one clock.
 */
export default function Flow({ curve, swings, away, home, source, stampMs, nowMs }) {
  const note = curve === null ? '' : `${sourceAge(source, stampMs, nowMs)} · up = ${home}`
  return (
    <section className="d2d-hoops__panel d2d-hoops__flowpanel" aria-label="Flow" data-testid="hoops-flow">
      {curve === null ? null : <Curve curve={curve} away={away} home={home} note={note} />}
      {swings.length === 0 ? null : <Swings swings={swings} />}
    </section>
  )
}
